import { Nullable, PartialNullable, RadPromiseLike } from "../Extensions";
import { PathHelper } from "../Helpers/PathHelper";
import { IPromisePublic, PromiseHelper } from "../Helpers/PromiseHelper";
import { StringHelper } from "../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../JNative";
import { PropertyChange } from "../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32, PropertyObject, RadObject, RadObjectPropertyValues } from "../RadObject";
import { ApplicationInformationService, WindowShim } from "../RadWindow";
import { AuthenticationData } from "../Server/AuthenticationData";
import { ServerErrorHelper } from "../Server/ServerErrorHelper";
import { ServiceManager } from "../ServiceManager";
import { FOCUSABLE_ELEMENTS_SELECTOR } from "./Controls/Control";
import { IDialogResponse } from "./Dialogs/IDialogResponse";
import { HistoryActionEnum } from "./HistoryActionEnum";
import { INavigationQueueItem } from "./INavigationQueueItem";
import { IPage, Page, PagePropertyValues } from "./Pages/Page";
import { IPageTransitioner, PageTransitioner } from "./PageTransitioner";
import { RadHistory } from "./RadHistory";
import { NAMESPACE } from "./_Namespace";

const
    radWindow = WindowShim();

export type FrameworkPageType = "index" | "details";
export type FrameworkEntityPageType = { entityName: string, pageType: FrameworkPageType };

export type PageManagerRouteConfiguration = { [key: string]: (() => Promise<typeof Page<PagePropertyValues>>) | FrameworkEntityPageType };
export type PageManagerEntityPageRouteConfiguration = { [key: string]: () => Promise<typeof Page<PagePropertyValues>> };

export type PageManagerConfiguration = {
    detailsPageMap: PageManagerEntityPageRouteConfiguration;
    indexPageMap: PageManagerEntityPageRouteConfiguration;
    routes: PageManagerRouteConfiguration;
}

export type PageManagerPropertyValues =
    RadObjectPropertyValues
    & {
        authenticationData: any;
        configuration: PageManagerConfiguration;
        currentPage: IPage;
        maximumNumberOfPages: number;
        navigationSuspended: boolean;
        onAuthenticatedAsync: (data: any) => void;
        onRequestAuthenticationData: () => AuthenticationData;
        onUnauthenticatedAsync: () => RadPromiseLike<any>;
        pageTransitioner: IPageTransitioner;
        scrollBarDimensions: { height: number, width: number };
    };

export type PageManagerProperties = {
    [name in keyof PageManagerPropertyValues]: Property<PageManagerPropertyValues[name]>
};

export interface IPageManager extends PageManagerProperties
{
    // #region Global Variables

    $$pageContainer: JNative<HTMLElement>;

    // #endregion

    // #region Methods

    registerBlockNavigationFunction(fn: (promise: IPromisePublic) => boolean): void;

    // #endregion
}

export class PageManager extends RadObject implements IPageManager
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PageManager";
    public static override className = "PageManager";

    // #region Properties

    public static instance: PageManager;

    // #endregion

    // #region Constructor

    static {
        $$(() =>
        {
            $$(document).on("click", "a", (evt: IJNativeEvent) =>
            {
                if (!PageManager.instance)
                    return;

                const
                    currentTarget = evt.currentTarget;

                if (!(currentTarget instanceof HTMLElement))
                    return;

                const
                    target = currentTarget.getAttribute("target"),
                    href = currentTarget.getAttribute("href");

                if (target && target !== "_self")
                    return;

                if (!href || href === "#")
                    return;

                // for #'s, if an anchor exists with this href as it's name, bail out...
                if (isHashTargettingAnchorOnPage(href))
                    return;

                const
                    newLocation = PathHelper.getLocation(href);

                if (window.location.origin !== newLocation.origin)
                    return;

                if (newLocation.pathname + newLocation.search !== location.pathname + location.search)
                    PageManager.instance.navigateAsync(newLocation.pathname + newLocation.search + newLocation.hash);

                evt.preventDefault();
            });

            let
                cancellingNavigation = false;

            $$(window).on("popstate", (evt: IJNativeEvent) =>
            {
                if (!PageManager.instance)
                    return;

                // for #'s, if an anchor exists with this href as it's name, bail out...
                if (isHashTargettingAnchorOnPage(window.location.hash))
                    return;

                const
                    state = (evt.originalEvent as PopStateEvent).state || { navigationData: null },
                    pageCounter = state.counter ?? 0,
                    back = pageCounter < RadHistory.pushStateCounter;

                PageManager.instance._setPushStateCounter(pageCounter);

                if (PageManager.instance._clearingHistory)
                {
                    PageManager.instance._clearingHistory = false;
                    PageManager.instance._historyCleared();
                    return;
                }

                if (cancellingNavigation)
                {
                    cancellingNavigation = false;
                    return;
                }

                const
                    location = PathHelper.getLocation(window.location),
                    navigationQueueItem = {
                        back: back,
                        historyAction: HistoryActionEnum.none,
                        navigationData: state.navigationData || null,
                        url: location.pathname + location.search + location.hash
                    };

                if (PageManager.instance._navigationBlocked(navigationQueueItem))
                {
                    cancellingNavigation = true;

                    if (back)
                        window.history.forward();
                    else
                        window.history.back();

                    return;
                }

                PageManager.instance._queueNavigation(navigationQueueItem);
            });

            function isHashTargettingAnchorOnPage(hash: string)
            {
                return hash.startsWith("#") && $$("a[name='" + hash.replace("#", "") + "']").length > 0;
            }
        });
    }

    // #endregion

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PageManagerPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                maximumNumberOfPages: 20,
                navigationSuspended: false,
                //pageTransitioner: new PageTransitioner(),
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public $$pageContainer: JNative<HTMLElement>;

    public _blockNavigationFunctions: ((promise: IPromisePublic) => boolean)[];
    protected _clearingHistory: boolean;
    protected _initializationPromises: Promise<any>[];
    protected _navigationHandlers: ((navigationQueueItem: INavigationQueueItem) => boolean)[];
    protected _navigationQueue: any[];
    protected _pagesOpenedAsDialog: any;
    protected _processingNavigationQueue: boolean;
    protected clearHistoryAsyncPromise?: IPromisePublic;
    protected currentNavigationQueueItem?: INavigationQueueItem;
    // TODO: BAM
    //protected progressLoader: FullPageLoader;

    // #endregion

    // #region Properties

    public authenticationData!: PropertyObject;
    public configuration!: Property<PageManagerConfiguration>;
    public currentPage!: Property<IPage>;
    public maximumNumberOfPages!: PropertyInt32;
    public navigationSuspended!: PropertyBoolean;
    public onAuthenticatedAsync!: Property<(data: any) => void>;
    public onRequestAuthenticationData!: Property<() => AuthenticationData>;
    public onUnauthenticatedAsync!: Property<() => RadPromiseLike<any>>;
    public pageTransitioner!: Property<IPageTransitioner>;
    public scrollBarDimensions!: Property<{ height: number, width: number }>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PageManagerPropertyValues>)
    {
        super(propertyValues);

        this._blockNavigationFunctions = [];
        this._clearingHistory = false;
        this._initializationPromises = [];
        this._navigationHandlers = [];
        this._navigationQueue = [];
        this._processingNavigationQueue = false;

        if (!this.pageTransitioner.get())
            this.pageTransitioner.set(new PageTransitioner());

        // TODO: BAM
        //this.progressLoader = new FullPageLoader({ mode: FullPageLoaderModeEnum.progress, total: 20, text: "{left} of {total} items remaining..." });

        ServiceManager.getRequired(ApplicationInformationService).queryStringParameters = {};

        const
            url = this._processQueryString();

        $$(document).trigger("SeriouslyRAD.PageManager:beforeInitialize", this);

        this._featureDetectScrollbars();
        this.$$pageContainer = $$(".rad-page-container");

        if (!this.$$pageContainer)
        {
            this.$$pageContainer =
                JNativeStatic.create$$({ nodeName: "main", attributes: { "class": "rad-page-container" } })
                    .appendTo(document.body);
        }

        if (this.$$pageContainer)
        {
            this.$$pageContainer
                .find(">.rad-control-page")
                .forEach(item =>
                {
                    if (!(item instanceof HTMLElement))
                        return;

                    item.setAttribute("data-no-cache", "true");
                    item.setAttribute("data-title", document.title);
                    item.setAttribute("data-url", "/" + PathHelper.getLocation(window.location).applicationRelativeUrl);
                });
        }

        if (!this._initializationPromises.length)
            setTimeout(() => { this._queueInitialNavigation(url); });
        else
        {
            // TODO: BAM
            //this.progressLoader.value.set(0);
            //this.progressLoader.total.set(this._initializationPromises.length);
            //this.progressLoader.enqueue();

            // TODO: BAM
            //this._initializationPromises
            //    .forEach(item => { item.then(() => { this.progressLoader.value.set((this.progressLoader.value.get() ?? 0) + 1); }); });

            Promise.all(this._initializationPromises)
                .then(() =>
                {
                    // TODO: BAM
                    //this.progressLoader.dequeue();
                    setTimeout(() => { this._queueInitialNavigation(url); });
                });
        }
    }

    // #endregion

    // #region Public Methods

    public clear(keepCurrentPage?: boolean)
    {
        const
            currentPage = this.currentPage.get();

        this.$$pageContainer
            .find(">.rad-control-page")
            .forEach(item =>
            {
                if (!(item instanceof HTMLElement))
                    return;

                const
                    page = JNativeStatic.radControl(item);

                if (!page)
                    return;

                if (!keepCurrentPage || page !== currentPage)
                    page.remove();
            });

        if (!keepCurrentPage)
            this.currentPage.set(null);
    }
    public clearHistoryAsync()
    {
        if (this.clearHistoryAsyncPromise)
            return this.clearHistoryAsyncPromise;

        this.clearHistoryAsyncPromise = PromiseHelper.createPromisePublic<any>();

        if (RadHistory.pushStateCounter > 0)
        {
            this._clearingHistory = true;
            window.history.go(RadHistory.pushStateCounter * -1);
        }
        else
        {
            setTimeout(() =>
            {
                this.clearHistoryAsyncPromise?.resolve();
                delete this.clearHistoryAsyncPromise;
            }, 100);
        }

        return (
            this.clearHistoryAsyncPromise
                .then(() =>
                {
                    this._navigationQueue = [];
                    this._processingNavigationQueue = false;
                })
        );
    }
    public clearPagesOpenedAsDialogs()
    {
        delete this._pagesOpenedAsDialog;
    }
    public async getPageAsDialogAsync(url: string, navigationData?: any)
    {
        const
            page = await this._loadPageViewAsync(url);

        page.navigationData.set(navigationData);
        page.$$element.get()?.css("display", StringHelper.empty);
        return page;
    }
    public getPageByUrl(url: string): Nullable<IPage>
    {
        let
            page = null;

        if (this.$$pageContainer)
        {
            this.$$pageContainer
                .find(">.rad-control-page")
                .forEach(item =>
                {
                    if (!(item instanceof HTMLElement))
                        return;

                    const
                        control = JNativeStatic.radControl(item);

                    if (control?.url.get() === url || item.getAttribute("data-url") === url)
                    {
                        page = control;
                        return false;
                    }
                });
        }

        return page;
    }
    public openPageAsDialogAsync(url: string, navigationData?: any, useCache?: boolean): RadPromiseLike<IDialogResponse>
    {
        if (!this._pagesOpenedAsDialog)
            this._pagesOpenedAsDialog = {};

        const
            loadPagePromise =
                useCache && this._pagesOpenedAsDialog[url]
                    ? Promise.resolve<IPage>(this._pagesOpenedAsDialog[url])
                    : this._loadPageViewAsync(url)
                        .then(page => { this._pagesOpenedAsDialog[url] = page; return page; });

        return (
            loadPagePromise
                .then(page =>
                {
                    page.navigationData.set(navigationData);
                    page.$$element.get()?.css("display", StringHelper.empty);
                    return page.openAsDialogAsync();
                })
        );
    }
    public navigateAsync(url: string, replace?: Nullable<boolean>, back?: Nullable<boolean>, navigationData?: any, onPageReady?: (page?: IPage) => void): IPromisePublic<any>
    {
        const
            promise = PromiseHelper.createPromisePublic<any>(),
            navigation: INavigationQueueItem = {
                back: !!back,
                promise: promise,
                historyAction: replace ? HistoryActionEnum.replace : HistoryActionEnum.push,
                navigationData: navigationData || null,
                onPageReady: onPageReady,
                url: url
            };

        let
            handled = false;

        for (let i = 0; i < this._navigationHandlers.length; i++)
        {
            if (this._navigationHandlers[i](navigation))
            {
                handled = true;
                break;
            }
        }

        if (!handled)
            this._queueNavigation(navigation);

        return promise;
    }
    public registerBlockNavigationFunction(fn: (promise: IPromisePublic) => boolean): void
    {
        if (this._blockNavigationFunctions.indexOf(fn) === -1)
            this._blockNavigationFunctions.push(fn);
    }
    public registerInitializationPromise(promise: Promise<any>)
    {
        this._initializationPromises.push(promise);
    }
    public registerNavigationHandler(fn: (navigationQueueItem: INavigationQueueItem) => boolean)
    {
        this._navigationHandlers.push(fn);
    }
    public reloadCurrentPageAsync()
    {
        const
            location = PathHelper.getLocation(window.location);

        let
            url = location.pathname;

        // Add initial trailing slash
        if (!url.endsWith("/") && !radWindow.cordova)
            url = url + "/";

        if (this._processingNavigationQueue && !this.currentNavigationQueueItem?.initialNavigation)
        {
            this._navigationQueue = [];
            this._processingNavigationQueue = false;
        }

        return this.navigateAsync(url + location.search + location.hash, true);
    }
    public replaceUrl(url: string)
    {
        history.replaceState(history.state, StringHelper.empty, url);
    }
    public resumeNavigation()
    {
        if (!this.navigationSuspended.get())
            return;

        this.navigationSuspended.set(false);
        this._processNavigationQueue();
    }
    public suspendNavigation()
    {
        this.navigationSuspended.set(true);
    }
    public unRegisterBlockNavigationFunction(fn: (promise: IPromisePublic) => boolean)
    {
        this._blockNavigationFunctions =
            this._blockNavigationFunctions.filter(item => item !== fn);
    }
    public unRegisterNavigationHandler(fn: (navigationQueueItem: INavigationQueueItem) => boolean)
    {
        this._navigationHandlers =
            this._navigationHandlers.filter(item => item !== fn);
    }

    // #endregion

    // #region Protected Methods

    protected _addAuthenticationHeaders(headers: any)
    {
        const
            onRequestAuthenticationData = this.onRequestAuthenticationData.get(),
            authenticationData = onRequestAuthenticationData ? onRequestAuthenticationData() : null;

        if (authenticationData)
        {
            switch (authenticationData.scheme)
            {
                case "Bearer":
                    if (authenticationData.accessToken)
                        headers["Authorization"] = `${authenticationData.scheme} ${authenticationData.accessToken}`;
                    break;
                case "Cookies":
                    if (authenticationData.antiForgeryToken)
                        headers["X-XSRF-Token"] = authenticationData.antiForgeryToken;
                    break;
            }
        }
    }
    protected _authenticationDataChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _backupTabIndexesIfPageDimmed(page: IPage)
    {
        /* TODO: This probably wants to be done by each Control,
           but then there's child controls to consider as we only
           want to run once per control + children initialization
        
           *Because performance. */

        if (!$$(document.body).hasClass("rad-state-dimmed"))
            return;

        /* INFO: If we're dimmed, the tabindex attributes are in a backup state.
           We need to save the tabindex to the backup attribute and set the
           tabindex to -1 */

        const
            $$page = page.$$element.get();

        if (!$$page)
            return

        $$page
            .find(".rad-control")
            .addBack()
            .each((index, element) =>
            {
                const
                    control = JNativeStatic.radControl(element as HTMLElement);

                if (!control || !control.dimmed?.set)
                    return;

                control.dimmed.set(true);
            });

        $$page
            .add($$page.find(FOCUSABLE_ELEMENTS_SELECTOR + ", [tabindex]"))
            .not("[data-tabindex-backup]")
            .each((index, element) =>
            {
                const
                    $$this = $$(element),
                    tabIndex = $$this.attr("tabindex");

                if (tabIndex !== undefined)
                    $$this.attr("data-tabindex-backup", tabIndex);

                $$this.attr("tabindex", "-1");
            });
    }
    protected _currentPageChanged(change: PropertyChange<IPage>)
    {
        /* Intentionally empty */
    }
    protected _removeOldPages()
    {
        const
            maximumNumberOfPages = this.maximumNumberOfPages.get();

        let
            numberOfPages = this.$$pageContainer?.children().length;

        while (numberOfPages && maximumNumberOfPages && numberOfPages > maximumNumberOfPages)
        {
            const
                $$page = this.$$pageContainer?.children().first();

            if ($$page)
                this._removePageByElement($$page);

            numberOfPages--;
        }
    }
    protected _removePageByElement($$page: JNative)
    {
        const
            page = $$page.radControl();

        if (page)
            page.remove();
        else
            $$page.remove();
    }
    protected _removePageByUrl(url: string)
    {
        const
            page = this.getPageByUrl(url);

        if (page)
            page.remove();
    }
    protected _featureDetectScrollbars()
    {
        const
            $$div = JNativeStatic.create$$({ nodeName: "div" }).css({ "height": "100px", "overflow": "scroll", "width": "100px" }),
            $$innerDiv = JNativeStatic.create$$({ nodeName: "div" }).css({ "height": "100%", "width": "100%" });

        $$div
            .append($$innerDiv)
            .appendTo(document.body);

        const
            scrollBarDimensions = {
                height: $$div[0].offsetHeight - $$div[0].clientHeight,
                width: $$div[0].offsetWidth - $$div[0].clientWidth
            };

        this.scrollBarDimensions.set(scrollBarDimensions);

        $$("html").addClass(
            scrollBarDimensions.width > 0
                ? "rad-feature-scrollbars-yes"
                : "rad-feature-scrollbars-no"
        );

        $$div.remove();
    }
    protected _getResourceUrl(url: string)
    {
        const
            serverPageInfo = {
                sourceUrl: url,
                destinationUrl: url
            };

        $$(document).triggerHandler("SeriouslyRAD.PageManager:MapServerPageUrl", serverPageInfo);

        const
            destinationLocation = PathHelper.getLocation(serverPageInfo.destinationUrl),
            isEmbeddedResource = destinationLocation.applicationRelativeUrl?.startsWith("embeddedresources/"),
            pathSlug =
                isEmbeddedResource && radWindow.cordova
                    ? StringHelper.empty
                    : "pages/";

        return `${ServiceManager.getRequired(ApplicationInformationService).virtualDirectory}${pathSlug}${destinationLocation.applicationRelativeUrl}`;
    }
    protected _historyCleared()
    {
        this.clearHistoryAsyncPromise?.resolve();
        delete this.clearHistoryAsyncPromise;
    }
    protected _loadPageViewAsync(url: string, promise?: IPromisePublic<IPage>)
    {
        const
            promiseInternal = promise ?? PromiseHelper.createPromisePublic<IPage>(),
            onAuthenticated = this.onAuthenticatedAsync.get(),
            route = this._getRoute(url);

        let
            data: any,
            headers = {},
            page: Page<PagePropertyValues>;

        this._addAuthenticationHeaders(headers);

        radWindow.fullPageLoader?.enqueue();

        route.getClassAsync()
            .then((pageClass) =>
            {
                page = new pageClass({ url: route.url, urlParameters: route.parameters });

                page.$$element.get()?.attr("data-url", route.url);
                page.hide();

                if (!page.requiresAuthentication.get())
                {
                    promiseInternal.resolve(page);
                    return;
                }

                return (
                    JNativeStatic
                        .ajax({
                            contentType: "application/json",
                            dataType: "json",
                            headers,
                            type: "POST",
                            url: ServiceManager.getRequired(ApplicationInformationService).serverVirtualDirectory + "api/Security/CheckLogin",
                            xhrFields: {
                                withCredentials: true
                            },
                        })
                        .then(results =>
                        {
                            const
                                dataInner = results.response,
                                xhrInner = results.xhr;

                            data = dataInner;

                            if (onAuthenticated)
                                return onAuthenticated(data);
                        })
                        .then(() =>
                        {
                            promiseInternal.resolve(page);
                        })
                );
            })
            .catch(xhrOrError =>
            {
                if (xhrOrError instanceof XMLHttpRequest)
                {
                    const
                        xhr = xhrOrError;

                    if (xhr.readyState === 0 || xhr.status <= 0 || xhr.status === 503 || !navigator.onLine)
                    {
                        let
                            title,
                            text;

                        if (xhr.status === 503)
                        {
                            title = "Server Unavailable";
                            text = "The server is currently unavailable.";
                        }
                        else
                        {
                            title = "Network Error";
                            text = "A network connection issue has been detected. Please ensure you have an internet connection and click \"OK\" to try again.";
                        }

                        $$(document).triggerHandler(
                            "Message",
                            {
                                text,
                                title,
                                promise:
                                    PromiseHelper.createPromisePublic()
                                        .then(() =>
                                        {
                                            setTimeout(() => { this._loadPageViewAsync(route.url, promiseInternal) });
                                        })
                            }
                        );

                        return;
                    }

                    if (xhr.status !== undefined)
                    {
                        if (xhr.status === 401)
                        {
                            const
                                onUnauthenticated = this.onUnauthenticatedAsync.get();

                            if (onUnauthenticated)
                            {
                                onUnauthenticated()
                                    .then(() =>
                                    {
                                        promiseInternal.resolve(page);
                                    })
                                    .catch((error) =>
                                    {
                                        promiseInternal.reject(error);
                                    })
                            }
                            else
                                promiseInternal.reject(xhr);
                        }
                        else
                        {
                            const
                                error = ServerErrorHelper.processError(xhr);

                            $$(document).triggerHandler("Error", { message: error });

                            promiseInternal.reject(error);
                        }

                        return;
                    }
                }

                $$(document).triggerHandler("Error", { message: xhrOrError });

                promiseInternal.reject(xhrOrError);
            })
            .finally(() =>
            {
                radWindow.fullPageLoader?.dequeue();
            });

        return promiseInternal;
    }
    protected _maximumNumberOfPagesChanged(change: PropertyChange<number>)
    {
        /* Intentionally empty */
    }
    protected _navigateAsync(navigationQueueItem: INavigationQueueItem)
    {
        return new Promise((resolve, reject) =>
        {
            let
                currentPage = this.currentPage.get();

            //if (!currentPage)
            //{
            //    currentPage = navigationQueueItem.page ?? null;
            //    this.currentPage.set(currentPage);
            //}

            if (currentPage === navigationQueueItem.page)
                resolve(undefined);
            else
            {
                navigationQueueItem.transitionType = ((navigationQueueItem.back ? currentPage : navigationQueueItem.page)?.transitionType.get()) ?? undefined;
                navigationQueueItem.transitionName = ((navigationQueueItem.back ? currentPage : navigationQueueItem.page)?.transitionName.get()) ?? undefined;
                navigationQueueItem.page?.navigationData.set(navigationQueueItem.navigationData);

                this._setScrollableForPage(navigationQueueItem.page);

                $$(document).triggerHandler("SeriouslyRAD.PageManager:Navigating", { currentPage: currentPage, newPage: navigationQueueItem.page, pageManager: this });

                this._showPageAsync(navigationQueueItem, currentPage)
                    .then(() =>
                    {
                        this._processQueryString();

                        if (navigationQueueItem.initialNavigation)
                            $$(document).trigger("SeriouslyRAD.PageManager:afterInitialize", self);

                        $$(document).triggerHandler("SeriouslyRAD.PageManager:Navigated", { pageManager: this });
                        navigationQueueItem.page?.triggerHandler("PageManagerAfterShowPage", { self: navigationQueueItem.page });
                        resolve(undefined);
                    })
                    .catch(error =>
                    {
                        $$(document).triggerHandler("SeriouslyRAD.PageManager:NavigateFailed", { currentPage: currentPage, pageManager: this });
                        this._setScrollableForPage(currentPage as IPage);
                        reject(error);
                    });
            }
        });
    }
    protected _navigationBlocked(navigationQueueItem: INavigationQueueItem)
    {
        const
            promise = PromiseHelper.createPromisePublic(),
            navigationBlocked = this._blockNavigationFunctions.some(item => item(promise) === true);

        if (navigationBlocked)
        {
            // If the promise is ever resolved, the navigation is re-attempted
            promise
                .then(() =>
                {
                    this._queueNavigation(navigationQueueItem);
                });
        }

        return navigationBlocked;
    }
    protected _navigationSuspendedChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _onAuthenticatedChanged(change: PropertyChange<(data: any) => void>)
    {
        /* Intentionally empty */
    }
    protected _onRequestAuthenticationDataChanged(change: PropertyChange<() => AuthenticationData>)
    {
        /* Intentionally empty */
    }
    protected _onUnauthenticatedChanged(change: PropertyChange<() => void>)
    {
        /* Intentionally empty */
    }
    protected _pageTransitionerChanged(change: PropertyChange<IPageTransitioner>)
    {
        /* Intentionally empty */
    }
    protected _processQueryString()
    {
        const
            location = PathHelper.getLocation(window.location),
            queryStringParameters = PathHelper.getQueryStringParameters(window.location);

        let
            url = location.pathname;

        // Add initial trailing slash
        if (!url.endsWith("/") && !radWindow.cordova)
            url = url + "/";

        if (queryStringParameters.length)
        {
            queryStringParameters
                .forEach(item => { ServiceManager.getRequired(ApplicationInformationService).queryStringParameters[item.name] = item.value; });

            history.replaceState({ counter: RadHistory.pushStateCounter, navigationData: null }, StringHelper.empty, url);
        }

        return url;
    }
    protected _processCacheControlHeader(cacheControlHeader: Nullable<string>)
    {
        const
            maxAgeRegex = /max-age=([\d]*)/,
            noCacheRegex = /(no-store|no-cache)/,
            noCache = cacheControlHeader === null ? false : noCacheRegex.test(cacheControlHeader),
            cacheExpiry = cacheControlHeader === null ? -1 : (maxAgeRegex.test(cacheControlHeader) ? maxAgeRegex.exec(cacheControlHeader)![1] : -1);

        return { noCache: noCache, cacheExpiry: cacheExpiry };
    }
    protected _processNavigationQueue()
    {
        if (this.navigationSuspended.get())
            return;

        if (!this._navigationQueue.length)
            return;

        if (this._processingNavigationQueue)
            return;

        this._processingNavigationQueue = true;

        let
            navigationQueueItem: INavigationQueueItem | undefined;

        // Skip intermediate navigations
        while (this._navigationQueue.length)
        {
            navigationQueueItem = this._navigationQueue.shift();

            if (this._navigationQueue.length && navigationQueueItem?.promise)
                navigationQueueItem.promise.resolve();
        }

        if (navigationQueueItem)
        {
            this._processNavigationQueueItem(navigationQueueItem)
                .then(() =>
                {
                    if (navigationQueueItem?.replaceUrl)
                        this.replaceUrl(navigationQueueItem.url);
                })
                .finally(() =>
                {
                    this._processingNavigationQueue = false;
                    this._processNavigationQueue();
                });
        }
    }
    protected async _processNavigationQueueItem(navigationQueueItem: INavigationQueueItem)
    {
        this.currentNavigationQueueItem = navigationQueueItem;

        const
            currentPage = this.currentPage.get();

        if (currentPage?.url.get() === navigationQueueItem.url)
        {
            delete this.currentNavigationQueueItem;
            return;
        }

        if (navigationQueueItem.url)
            navigationQueueItem.page = this.getPageByUrl(navigationQueueItem.url) ?? undefined

        if (!navigationQueueItem.page && navigationQueueItem.url)
        {
            try
            {
                navigationQueueItem.page = await this._loadPageViewAsync(navigationQueueItem.url);

                if (navigationQueueItem.onPageReady)
                    navigationQueueItem.onPageReady(navigationQueueItem.page);

                await this._navigateAsync(navigationQueueItem);
                navigationQueueItem.promise?.resolve();
            }
            catch (error)
            {
                navigationQueueItem.promise?.reject(error);
                throw error;
            }
            finally
            {
                delete this.currentNavigationQueueItem;
            }
        }
        else
        {
            navigationQueueItem.page?.$$element.get()?.css("display", "none");

            if (navigationQueueItem.onPageReady)
                navigationQueueItem.onPageReady(navigationQueueItem.page);

            try
            {
                await this._navigateAsync(navigationQueueItem);
                navigationQueueItem.promise?.resolve();
            }
            catch (error)
            {
                navigationQueueItem.promise?.reject();
                throw error;
            }
            finally
            {
                delete this.currentNavigationQueueItem;
            }
        }
    }
    protected _queueNavigation(navigationQueueItem: INavigationQueueItem)
    {
        if (this._navigationBlocked(navigationQueueItem))
            return;

        this._navigationQueue.push(navigationQueueItem);
        this._processNavigationQueue();
    }
    protected _queueInitialNavigation(url: string)
    {
        this._setPushStateCounter(history.state?.counter ?? 0);

        this._queueNavigation({
            initialNavigation: true,
            back: false,
            historyAction: HistoryActionEnum.none,
            navigationData: null,
            url: url,
            title: document.title,
            replaceUrl: !!window.location.hash
        });
    }
    protected _scrollBarDimensionsChanged(change: PropertyChange<{ height: number, width: number }>)
    {
        /* Intentionally empty */
    }
    protected _setPushStateCounter(value: number)
    {
        RadHistory.pushStateCounter = value;

        if (RadHistory.pushStateCounter === 0)
            $$("html").addClass("rad-state-back-hidden");
        else
            $$("html").removeClass("rad-state-back-hidden");
    }
    protected _setScrollableForPage(page?: IPage)
    {
        if (page?.scrollable.get())
            this.$$pageContainer?.css({ "overflow-y": "hidden" });
        else
            this.$$pageContainer?.css({ "overflow-y": StringHelper.empty });
    }
    protected _showPageAsync(navigationQueueItem: INavigationQueueItem, currentPage: Nullable<IPage>)
    {
        return new Promise((resolve, reject) =>
        {
            let
                pageLoadAndOrActivatePromise;

            if (navigationQueueItem.page)
                this._backupTabIndexesIfPageDimmed(navigationQueueItem.page);

            if (!currentPage)
            {
                navigationQueueItem.page?.$$element.get()?.appendTo(this.$$pageContainer);
                navigationQueueItem.page?.$$element.get()?.css("display", StringHelper.empty);
                navigationQueueItem.page?.show();

                if (!(navigationQueueItem.page as any)[".loaded"])
                {
                    pageLoadAndOrActivatePromise =
                        navigationQueueItem.page?.loadAsync()
                            .then(() =>
                            {
                                (navigationQueueItem.page as any)[".loaded"] = true;
                                return navigationQueueItem.page?.activateAsync();
                            });
                }
                else
                    pageLoadAndOrActivatePromise = navigationQueueItem.page?.activateAsync();

                if (pageLoadAndOrActivatePromise)
                {
                    pageLoadAndOrActivatePromise
                        .then(() =>
                        {
                            this._updateCurrentPageFromNavigationQueueItem(navigationQueueItem);
                            this._removeOldPages();
                            resolve(undefined);
                        })
                        .catch(error =>
                        {
                            reject(error);
                        });
                }
            }
            else
            {
                const
                    pageTransitioner = this.pageTransitioner.get();

                if (!pageTransitioner)
                {
                    reject();
                    return;
                }

                pageTransitioner.executeAsync(this, navigationQueueItem)
                    .then(() =>
                    {
                        return currentPage.deactivateAsync();
                    })
                    .then(() =>
                    {
                        this._updateCurrentPageFromNavigationQueueItem(navigationQueueItem);
                        this._removeOldPages();
                        resolve(undefined);
                    })
                    .catch(error =>
                    {
                        reject(error);
                    });
            }
        });
    }
    protected _updateCurrentPageFromNavigationQueueItem(navigationQueueItem: INavigationQueueItem)
    {
        if (RadHistory.pushStateCounter === undefined)
            this._setPushStateCounter(history.state ? history.state.counter : 0);

        switch (navigationQueueItem.historyAction)
        {
            case HistoryActionEnum.push:
                this._setPushStateCounter(RadHistory.pushStateCounter + 1);
                history.pushState({ counter: RadHistory.pushStateCounter, navigationData: navigationQueueItem.navigationData }, StringHelper.empty, navigationQueueItem.page?.url.get());
                break;
            case HistoryActionEnum.replace:
                history.replaceState({ counter: RadHistory.pushStateCounter, navigationData: navigationQueueItem.navigationData }, StringHelper.empty, navigationQueueItem.page?.url.get());
                break;
        }

        this._updateDocumentTitle(navigationQueueItem.page?.title.get() ?? undefined);

        this.currentPage.set(navigationQueueItem.page ?? null, false);
    }
    protected _updateDocumentTitle(title?: string)
    {
        const
            stringTitle = title ? title : StringHelper.empty;

        $$("title").html(StringHelper.htmlEncode(stringTitle));
        document.title = stringTitle;
    }

    // #endregion

    // #region Private Methods

    private _getRoute(url: string)
    {
        const
            configuration = this.configuration.get();

        if (!configuration)
            throw new Error("Configuration not set");

        const
            routes = configuration.routes;

        if (!routes)
            throw new Error("Routes have not been configured");

        const
            urlsToTry = [url];

        if (!StringHelper.endsWith(url, "/"))
            urlsToTry.push(url + "/");

        for (const urlToTry of urlsToTry)
        {
            const
                urlParts = urlToTry.split("/"),
                routeKeyArray = Object.keys(routes);

            for (const routeKey of routeKeyArray)
            {
                const
                    routeParameters: { [key: string]: string } = {},
                    routeParts = routeKey.split("/");

                if (routeParts.length !== urlParts.length)
                    continue;

                let
                    routeIsMatch = true;

                for (let i = 0; i < routeParts.length; i++)
                {
                    const
                        routePart = routeParts[i],
                        urlPart = urlParts[i],
                        partIsMatch =
                            urlPart === routePart
                            || routePart.length > 2 && routePart.startsWith("{") && routePart.endsWith("}");

                    if (!partIsMatch)
                    {
                        routeIsMatch = false;
                        break;
                    }

                    if (urlPart !== routePart)
                        routeParameters[routePart.substring(1, routePart.length - 1)] = urlPart;
                }

                if (!routeIsMatch)
                    continue;

                const
                    route = routes[routeKey],
                    getClassAsync =
                        typeof route === "object"
                            ? this._resolveFrameworkEntityPageType(route)
                            : route;

                if (!getClassAsync)
                    throw new Error(`Page class for url not found (${url})`);

                return {
                    getClassAsync: getClassAsync,
                    parameters: routeParameters,
                    url: urlToTry,
                };
            }
        }

        throw new Error(`Route for url not found (${url})`);
    }
    private _resolveFrameworkEntityPageType(frameworkEntityPageType: FrameworkEntityPageType): (() => Promise<typeof Page<PagePropertyValues>>) | undefined
    {
        const
            configuration = this.configuration.get();

        if (!configuration)
            throw new Error("Configuration not set");

        switch (frameworkEntityPageType.pageType)
        {
            case "details":
                return configuration.detailsPageMap[frameworkEntityPageType.entityName];
            case "index":
                return configuration.indexPageMap[frameworkEntityPageType.entityName];
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PageManagerPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.authenticationData = new PropertyObject(this, propertyValues.authenticationData, this._authenticationDataChanged);
        this.configuration = new Property<PageManagerConfiguration>(this, "System.Object, System.Private.CoreLib", propertyValues.configuration);
        this.currentPage = new Property<IPage>(this, "System.Object, System.Private.CoreLib", propertyValues.currentPage, this._currentPageChanged);
        this.maximumNumberOfPages = new PropertyInt32(this, propertyValues.maximumNumberOfPages, this._maximumNumberOfPagesChanged);
        this.navigationSuspended = new PropertyBoolean(this, propertyValues.navigationSuspended, this._navigationSuspendedChanged);
        this.onAuthenticatedAsync = new Property<(data: any) => void>(this, "System.Object, System.Private.CoreLib", propertyValues.onAuthenticatedAsync, this._onAuthenticatedChanged);
        this.onRequestAuthenticationData = new Property<() => AuthenticationData>(this, "System.Object, System.Private.CoreLib", propertyValues.onRequestAuthenticationData, this._onRequestAuthenticationDataChanged);
        this.onUnauthenticatedAsync = new Property<() => RadPromiseLike<any>>(this, "System.Object, System.Private.CoreLib", propertyValues.onUnauthenticatedAsync, this._onUnauthenticatedChanged);
        this.pageTransitioner = new Property<IPageTransitioner>(this, "System.Object, System.Private.CoreLib", propertyValues.pageTransitioner, this._pageTransitionerChanged);
        this.scrollBarDimensions = new Property<{ height: number, width: number }>(this, "System.Object, System.Private.CoreLib", propertyValues.scrollBarDimensions, this._scrollBarDimensionsChanged);
    }

    // #endregion

    // #endregion
}